/**
 * 90mod Offline Operation Queue
 * ─────────────────────────────
 * Intercepts all mutating fetch() calls (PATCH, POST, PUT, DELETE).
 * When offline: queues the operation in localStorage, shows branded UI feedback.
 * When back online: replays the queue against POST /api/v1/offline/sync.
 *
 * Architecture:
 *   - Each queued op: { id, method, url, body, headers_snapshot, queued_at, client_id }
 *   - On reconnect: POST batch to /api/v1/offline/sync
 *   - Server returns: { applied: [...], conflicts: [...] }
 *   - Conflicts shown per-field in a resolution UI
 *
 * This file is loaded once from application.js and wraps window.fetch globally.
 * No import of this into Stimulus controllers needed — it's transparent.
 */

const MOD_OFFLINE_QUEUE_KEY = "_90mod_opq";
const MOD_LAST_SYNC_KEY     = "_90mod_last_sync_at";
const MOD_CLIENT_ID_KEY     = "_90mod_client_id";

// ─── Client identity ────────────────────────────────────────────────────────
function getClientId() {
  let id = localStorage.getItem(MOD_CLIENT_ID_KEY);
  if (!id) {
    id = "clt_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    localStorage.setItem(MOD_CLIENT_ID_KEY, id);
  }
  return id;
}

// ─── Queue helpers ───────────────────────────────────────────────────────────
function loadQueue() {
  try {
    return JSON.parse(localStorage.getItem(MOD_OFFLINE_QUEUE_KEY) || "[]");
  } catch (_) {
    return [];
  }
}

function saveQueue(ops) {
  localStorage.setItem(MOD_OFFLINE_QUEUE_KEY, JSON.stringify(ops));
}

function enqueue(method, url, body, headers) {
  const ops = loadQueue();
  const op = {
    id:          "op_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    method:      method.toUpperCase(),
    url:         url,
    body:        body,
    headers:     headers,
    queued_at:   new Date().toISOString(),
    client_id:   getClientId(),
  };
  ops.push(op);
  saveQueue(ops);
  updateQueueBadge(ops.length);
  requestBackgroundSync();
  return op;
}

function requestBackgroundSync() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.ready
    .then((registration) => registration.sync?.register('sync-operations'))
    .catch(() => { /* Background Sync is optional; online events still replay. */ });
}

function clearQueue() {
  localStorage.removeItem(MOD_OFFLINE_QUEUE_KEY);
  updateQueueBadge(0);
}

// ─── Badge UI ─────────────────────────────────────────────────────────────────
function updateQueueBadge(count) {
  let badge = document.getElementById("_90mod_queue_badge");
  if (count === 0) {
    if (badge) badge.remove();
    return;
  }
  if (!badge) {
    badge = document.createElement("div");
    badge.id = "_90mod_queue_badge";
    badge.style.cssText = `
      position: fixed; bottom: 16px; left: 16px; z-index: 999997;
      background: rgba(26,26,46,0.92); border: 1px solid rgba(200,149,108,0.4);
      border-radius: 100px; padding: 5px 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 0.78rem; color: #c8956c; backdrop-filter: blur(8px);
      display: flex; align-items: center; gap: 6px;
      box-shadow: 0 2px 12px rgba(0,0,0,0.3);
    `;
    document.body.appendChild(badge);
  }
  badge.innerHTML = `
    <span style="width:7px;height:7px;background:#c8956c;border-radius:50%;display:inline-block;"></span>
    <span>${count} change${count === 1 ? "" : "s"} queued offline</span>
  `;
}

// ─── Conflict resolution UI ───────────────────────────────────────────────────
function showConflictResolutionUI(conflicts) {
  if (!conflicts || conflicts.length === 0) return;

  // Remove any existing conflict UI
  const existing = document.getElementById("_90mod_conflict_ui");
  if (existing) existing.remove();

  const overlay = document.createElement("div");
  overlay.id = "_90mod_conflict_ui";
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 999990;
    background: rgba(10,10,20,0.75); backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    padding: 20px;
  `;

  const dialog = document.createElement("div");
  dialog.style.cssText = `
    background: #1a1a2e; border: 1px solid rgba(200,149,108,0.3);
    border-radius: 12px; padding: 28px 32px; max-width: 540px; width: 100%;
    max-height: 80vh; overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  `;

  const conflictItems = conflicts.map((c, i) => `
    <div style="border: 1px solid rgba(200,149,108,0.2); border-radius: 8px;
                padding: 14px 16px; margin-bottom: 12px; background: rgba(200,149,108,0.04);">
      <div style="font-size:0.78rem; color:#8080a0; margin-bottom: 8px; font-weight:500;">
        ${c.entity_type} · ${c.entity_id} · field: <strong style="color:#c0c0d8">${c.field}</strong>
      </div>
      <div style="display:flex; gap: 10px; margin-bottom: 10px;">
        <div style="flex:1; background:rgba(255,255,255,0.04); border-radius:6px; padding:10px;">
          <div style="font-size:0.7rem; color:#6060a0; margin-bottom:4px;">YOUR VERSION</div>
          <div style="font-size:0.88rem; color:#e0e0f0; word-break:break-all;">${escapeHtml(String(c.client_value ?? "—"))}</div>
        </div>
        <div style="flex:1; background:rgba(255,255,255,0.04); border-radius:6px; padding:10px;">
          <div style="font-size:0.7rem; color:#6060a0; margin-bottom:4px;">TEAMMATE'S VERSION</div>
          <div style="font-size:0.88rem; color:#e0e0f0; word-break:break-all;">${escapeHtml(String(c.server_value ?? "—"))}</div>
        </div>
      </div>
      <div style="display:flex; gap:8px;">
        <button data-conflict="${i}" data-choice="mine"
          style="flex:1; background:rgba(200,149,108,0.15); border:1px solid rgba(200,149,108,0.4);
                 color:#c8956c; border-radius:6px; padding:7px; font-size:0.8rem; cursor:pointer;
                 font-weight:600;">
          Keep mine
        </button>
        <button data-conflict="${i}" data-choice="theirs"
          style="flex:1; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.12);
                 color:#c0c0d8; border-radius:6px; padding:7px; font-size:0.8rem; cursor:pointer;">
          Keep teammate's
        </button>
      </div>
    </div>
  `).join("");

  dialog.innerHTML = `
    <div style="display:flex; align-items:center; gap:10px; margin-bottom:20px;">
      <span style="font-size:1.1rem; font-weight:700; color:#c8956c;">90mod</span>
      <span style="font-size:1.1rem; font-weight:700; color:#fff;">Sync conflict${conflicts.length > 1 ? "s" : ""}</span>
      <span style="margin-left:auto; background:rgba(200,149,108,0.15); color:#c8956c;
                   border:1px solid rgba(200,149,108,0.3); border-radius:100px; padding:3px 10px;
                   font-size:0.75rem; font-weight:600;">${conflicts.length} to resolve</span>
    </div>
    <p style="font-size:0.875rem; color:#8080a0; margin-bottom:20px; line-height:1.6;">
      While you were offline, a teammate also edited some of the same fields.
      Choose which version to keep for each conflict below.
    </p>
    <div id="_90mod_conflict_list">${conflictItems}</div>
    <div id="_90mod_conflict_status" style="font-size:0.8rem; color:#6060a0; margin-top:12px;"></div>
    <button id="_90mod_conflict_done"
      style="margin-top:16px; width:100%; background:#c8956c; color:#1a1a2e; border:none;
             border-radius:8px; padding:10px; font-weight:700; font-size:0.9rem; cursor:pointer;"
      disabled>
      Apply resolutions
    </button>
  `;

  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  // Wire up choices
  const resolutions = {};
  const total = conflicts.length;

  dialog.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-conflict][data-choice]");
    if (!btn) return;

    const idx   = parseInt(btn.dataset.conflict, 10);
    const choice = btn.dataset.choice; // "mine" | "theirs"

    resolutions[idx] = choice;

    // Visual feedback — highlight chosen button
    const row = btn.closest("div[style*='border']");
    row.querySelectorAll("[data-choice]").forEach(b => {
      b.style.opacity = "0.5";
    });
    btn.style.opacity = "1";
    btn.style.outline = "2px solid #c8956c";

    const doneBtn = document.getElementById("_90mod_conflict_done");
    if (Object.keys(resolutions).length === total) {
      doneBtn.disabled = false;
    }
  });

  document.getElementById("_90mod_conflict_done").addEventListener("click", async () => {
    const status = document.getElementById("_90mod_conflict_status");
    status.textContent = "Applying…";

    const resolved = conflicts.map((c, i) => ({
      entity_type:  c.entity_type,
      entity_id:    c.entity_id,
      field:        c.field,
      chosen_value: resolutions[i] === "mine" ? c.client_value : c.server_value,
      resolution:   resolutions[i]
    }));

    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content;
      const resp = await fetch("/api/v1/offline/resolve", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        },
        body: JSON.stringify({ resolutions: resolved })
      });

      if (resp.ok) {
        status.textContent = "✓ All conflicts resolved";
        setTimeout(() => {
          overlay.remove();
          window.location.reload(); // Reload to get fresh server state
        }, 800);
      } else {
        status.textContent = "Error applying — please refresh.";
      }
    } catch (err) {
      status.textContent = "Network error: " + err.message;
    }
  });
}

function escapeHtml(str) {
  const d = document.createElement("div");
  d.appendChild(document.createTextNode(str));
  return d.innerHTML;
}

// ─── Replay queued ops on reconnect ──────────────────────────────────────────
async function replayQueue() {
  const ops = loadQueue();
  if (ops.length === 0) return;

  console.debug(`[90mod offline] Replaying ${ops.length} queued operation(s)…`);

  const csrf = document.querySelector("meta[name='csrf-token']")?.content;
  const processedIds = new Set();
  let syncedCount = 0;

  // Replay the original requests so each normal controller applies its own
  // authorization, validation, callbacks, and create/delete semantics.
  for (const op of ops) {
    const headers = new Headers(op.headers || {});
    headers.set("Accept", headers.get("Accept") || "application/json");
    if (csrf) headers.set("X-CSRF-Token", csrf);

    const requestOptions = { method: op.method, headers, credentials: "same-origin" };
    if (op.body !== null && op.body !== undefined) {
      requestOptions.body = typeof op.body === "string" ? op.body : JSON.stringify(op.body);
      if (!headers.has("Content-Type") && typeof op.body !== "string") {
        headers.set("Content-Type", "application/json");
      }
    }

    try {
      const response = await _originalFetch(op.url, requestOptions);
      if (response.ok) {
        processedIds.add(op.id);
        syncedCount += 1;
      } else if (response.status >= 400 && response.status < 500) {
        // Validation/authorization failures will not improve on retry. Remove
        // the operation and report it rather than trapping the queue forever.
        processedIds.add(op.id);
        console.error(`[90mod offline] Rejected ${op.method} ${op.url}: HTTP ${response.status}`);
      } else {
        break;
      }
    } catch (err) {
      console.warn("[90mod offline] Replay paused:", err.message);
      break;
    }
  }

  const remaining = loadQueue().filter(op => !processedIds.has(op.id));
  saveQueue(remaining);
  updateQueueBadge(remaining.length);

  if (syncedCount > 0) {
    localStorage.setItem(MOD_LAST_SYNC_KEY, new Date().toISOString());
    showSyncSuccessToast(syncedCount);
  }
}

function showSyncSuccessToast(count) {
  // Inject keyframes once
  if (!document.getElementById("_90mod_keyframes")) {
    const style = document.createElement("style");
    style.id = "_90mod_keyframes";
    style.textContent = `
      @keyframes _90mod_fadein {
        from { opacity: 0; transform: translateX(-50%) translateY(8px); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }

  const toast = document.createElement("div");
  toast.style.cssText = `
    position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
    z-index: 999997; background: #1a1a2e; border: 1px solid rgba(200,149,108,0.4);
    border-radius: 100px; padding: 8px 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.82rem; color: #c8956c; display:flex; align-items:center; gap:8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4); animation: _90mod_fadein 0.2s ease;
  `;
  toast.innerHTML = `<span>✓</span><span>${count} offline change${count === 1 ? "" : "s"} synced</span>`;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
}

// ─── Mutating fetch interceptor ───────────────────────────────────────────────
const MUTATING_METHODS = new Set(["PATCH", "POST", "PUT", "DELETE"]);
const SKIP_URLS = ["/api/v1/offline/", "/cable", "/auth/", "api.ipify"];

const _originalFetch = window.fetch.bind(window);

window.fetch = async function interceptedFetch(input, init = {}) {
  const method = (init.method || "GET").toUpperCase();
  const url    = typeof input === "string" ? input : input?.url;

  if (!url || new URL(url, window.location.href).origin !== window.location.origin) {
    return _originalFetch(input, init);
  }

  // Only intercept mutating requests to our own app
  if (!MUTATING_METHODS.has(method)) {
    return _originalFetch(input, init);
  }

  // Skip internal sync/auth endpoints to prevent loops
  if (SKIP_URLS.some(skip => url?.includes(skip))) {
    return _originalFetch(input, init);
  }

  // If we're online — pass through normally
  if (navigator.onLine) {
    try {
      const resp = await _originalFetch(input, init);
      // Record last successful sync time on any successful mutating response
      if (resp.ok) {
        localStorage.setItem(MOD_LAST_SYNC_KEY, Date.now().toString());
      }
      return resp;
    } catch (err) {
      // Network failure mid-request (e.g. wifi dropped mid-flight)
      // Treat like offline — queue and show user feedback
      if (!navigator.onLine) {
        queueAndNotify(method, url, init);
        return syntheticOfflineResponse();
      }
      throw err;
    }
  }

  // Offline — queue the operation
  queueAndNotify(method, url, init);
  return syntheticOfflineResponse();
};

function queueAndNotify(method, url, init) {
  let body = null;
  try {
    body = init.body ? JSON.parse(init.body) : null;
  } catch (_) {
    body = init.body;
  }

  const headers = {};
  if (init.headers) {
    const h = new Headers(init.headers);
    h.forEach((v, k) => {
      // Don't store CSRF token — will refresh on replay
      if (k.toLowerCase() !== "x-csrf-token") headers[k] = v;
    });
  }

  const op = enqueue(method, url, body, headers);
  console.debug(`[90mod offline] Queued op ${op.id}: ${method} ${url}`);
}

function syntheticOfflineResponse() {
  // Return a synthetic 200 response so UI doesn't show an error.
  // The actual mutation will be replayed when online.
  return new Response(
    JSON.stringify({ queued_offline: true, message: "Saved offline — will sync when connected." }),
    { status: 200, headers: { "Content-Type": "application/json" } }
  );
}

// ─── Online/offline event wiring ─────────────────────────────────────────────
window.addEventListener("online",  () => {
  console.debug("[90mod offline] Back online — replaying queue…");
  replayQueue();
});

window.addEventListener("offline", () => {
  console.debug("[90mod offline] Went offline — mutations will be queued.");
});

navigator.serviceWorker?.addEventListener("message", (event) => {
  if (event.data?.type === "REPLAY_OFFLINE_QUEUE" && navigator.onLine) replayQueue();
});

// ─── On page load: if queue has ops, replay immediately ──────────────────────
// (handles the case where the app was closed while offline and reopened online)
document.addEventListener("DOMContentLoaded", () => {
  const ops = loadQueue();
  if (ops.length > 0) {
    updateQueueBadge(ops.length);
    if (navigator.onLine) {
      replayQueue();
    }
  }
});

// ─── Turbo Drive: strip transient DOM nodes before Turbo caches the page ─────
// The badge and conflict modal are dynamically injected — if Turbo snapshots
// the page with them present, they'll flash on restore. Clean them up first.
document.addEventListener("turbo:before-cache", () => {
  document.getElementById("_90mod_queue_badge")?.remove();
  document.getElementById("_90mod_conflict_ui")?.remove();
  // Leave _90mod_keyframes — it's in <head>, not body, and harmless in snapshots
});

export { loadQueue, clearQueue, getClientId };
